import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';

const MINUTES = ['00', '15', '30', '45'];
const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));

const ALL_TIMES = [];
for (const h of HOURS) {
  for (const m of MINUTES) {
    ALL_TIMES.push(`${h}:${m}`);
  }
}

const snapToQuarter = (val) => {
  if (!val) return '';
  const match = val.match(/^(\d{1,2}):?(\d{0,2})$/);
  if (!match) return '';
  let h = parseInt(match[1]) || 0;
  let m = parseInt(match[2]) || 0;
  h = Math.max(0, Math.min(23, h));
  const snapped = MINUTES.reduce((prev, curr) =>
    Math.abs(parseInt(curr) - m) < Math.abs(parseInt(prev) - m) ? curr : prev
  );
  return `${String(h).padStart(2, '0')}:${snapped}`;
};

const TimeInput = ({ value = '', onChange, className = '', ...rest }) => {
  const [open, setOpen] = useState(false);
  const [inputVal, setInputVal] = useState(value);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (value !== inputVal) {
      setInputVal(value);
    }
    // inputVal utelämnad MED AVSIKT och det är viktigt. Effekten synkar propen
    // value till lokalt state. Tas inputVal med skulle effekten köra vid varje
    // tangenttryck, se att value skiljer sig, och skriva tillbaka det gamla
    // värdet — man skulle inte kunna skriva i fältet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Beräkna dropdown-position relativt viewport
  const updatePosition = useCallback(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropdownHeight = 192; // max-h-48 = 12rem = 192px
      const showAbove = spaceBelow < dropdownHeight && rect.top > dropdownHeight;

      setDropdownPos({
        top: showAbove ? rect.top - dropdownHeight - 4 : rect.bottom + 4,
        left: rect.left,
        width: rect.width
      });
    }
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        // Kolla även om klicket var i dropdown-portalen
        if (listRef.current && listRef.current.contains(e.target)) return;
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (open) {
      updatePosition();
      // Scrolla till valt värde
      requestAnimationFrame(() => {
        if (listRef.current) {
          const scrollTo = inputVal || '08:00';
          const idx = ALL_TIMES.indexOf(scrollTo);
          if (idx >= 0) {
            const item = listRef.current.children[idx];
            if (item) {
              item.scrollIntoView({ block: 'center' });
            }
          }
        }
      });
    }
    // inputVal utelämnad med avsikt: listan ska bara reagera på att dropdownen
    // öppnas, inte på varje tangenttryck i fältet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, updatePosition]);

  const emit = (val) => {
    if (onChange) {
      onChange({ target: { value: val } });
    }
  };

  const handleBlur = (e) => {
    // Ignorera blur om man klickar i dropdown-portalen
    if (listRef.current && listRef.current.contains(e.relatedTarget)) return;
    if (!inputVal) return;
    const snapped = snapToQuarter(inputVal);
    setInputVal(snapped);
    emit(snapped);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
      setOpen(false);
    }
    if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const handleSelect = (time) => {
    setInputVal(time);
    emit(time);
    setOpen(false);
  };

  const dropdown = open ? ReactDOM.createPortal(
    <ul
      ref={listRef}
      style={{
        position: 'fixed',
        top: dropdownPos.top,
        left: dropdownPos.left,
        width: dropdownPos.width,
        zIndex: 99999
      }}
      className="max-h-48 overflow-y-auto bg-white border border-gray-300 rounded-lg shadow-lg py-1"
      onMouseDown={(e) => e.preventDefault()}
    >
      {ALL_TIMES.map((time) => (
        <li
          key={time}
          onClick={() => handleSelect(time)}
          className={`px-3 py-1.5 text-sm cursor-pointer hover:bg-primary/10 ${
            time === inputVal ? 'bg-primary/15 font-medium text-primary' : 'text-gray-700'
          }`}
        >
          {time}
        </li>
      ))}
    </ul>,
    document.body
  ) : null;

  return (
    <div ref={wrapperRef} className="relative" style={{ minWidth: 0 }}>
      <input
        ref={inputRef}
        type="text"
        value={inputVal}
        onChange={(e) => setInputVal(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder="HH:MM"
        className={className || "w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"}
        {...rest}
      />
      {dropdown}
    </div>
  );
};

export default TimeInput;
